import { specialtySlug, stateFromNameSlug, stateNameSlug, toSlug } from "@/lib/seo-slugs";
import { US_CITIES_VALID } from "@/data/us-cities";

export type ResolvedCity = { city: string; state: string };

// Resolves free-typed/committed city text ("Denver" or "Denver, CO") against
// Heallexa's own known-city list — no DB round trip, no dependency on the
// counts-autocomplete migration. Only resolves on an unambiguous single match.
export function resolveCityFromLabel(label: string): ResolvedCity | null {
  const bare = label.split(",")[0].trim().toLowerCase();
  if (!bare) return null;
  const matches = US_CITIES_VALID.filter((c) => c.name.toLowerCase() === bare);
  if (matches.length !== 1) return null;
  const state = stateFromNameSlug(matches[0].state);
  return state ? { city: matches[0].name, state: state.code } : null;
}

// Single source of truth for "given whatever combination of specialty/city is
// currently active, what's the canonical /providers/... URL for it?" — used
// by every /search and /providers/* page so committing a filter always lands
// on the most specific clean URL available, matching the reference site's
// continuous hierarchy (state/city/specialty, no query string) instead of
// falling back to query params whenever both dimensions are known.
// `to:` here intentionally has NO underscore even though the route file is
// providers.$state.$city_.$specialty.tsx — TanStack Router's generated types
// key navigation targets (`to:`) by the clean public PATH, and only the `id`
// (used for useSearch/useNavigate/{from:} inside that route file itself)
// carries the underscore. Mixing these up compiles fine (both are just
// strings cast through `as any` at the call sites) but silently fails to
// match the intended route at runtime.
export type ProvidersDestination =
  | { to: "/providers/$id"; params: { id: string } }
  | { to: "/providers/$state/$city"; params: { state: string; city: string } }
  | { to: "/providers/$state/$city/$specialty"; params: { state: string; city: string; specialty: string } };

export function providersRouteFor(
  specialty: string | undefined,
  city: ResolvedCity | undefined,
): ProvidersDestination | null {
  const state = city ? stateFromNameSlug(city.state) : undefined;
  if (specialty && city && state) {
    return {
      to: "/providers/$state/$city/$specialty",
      params: { state: stateNameSlug(state.name), city: toSlug(city.city), specialty: specialtySlug(specialty) },
    };
  }
  if (specialty) {
    return { to: "/providers/$id", params: { id: specialtySlug(specialty) } };
  }
  if (city && state) {
    return { to: "/providers/$state/$city", params: { state: stateNameSlug(state.name), city: toSlug(city.city) } };
  }
  return null;
}
